import { PROMPT_DASH } from "../../witness/interact";

/*
 * The prompts: what the key under your hand would do (chunk U-0's split of
 * `panels.ts`).
 *
 * Six instances of one class, told apart by `variant`, and that is the whole
 * design: one anatomy, six questions. It is also the one thing in the HUD
 * that changes every frame, which is why it writes nothing when the text has
 * not moved.
 *
 * # The shape of a prompt line, and who spells it (chunk U-1)
 *
 * **`<key>` — `<what pressing it does>`.** Four modules in this client compose
 * one and every one of them writes that em-dash:
 *
 * - `witness/interact.ts`'s `promptFor` — *F — talk to Halvard Reedmere*;
 * - `hud/crossing.ts`'s three labels — *C — make camp until morning*;
 * - `witness/arms.ts`'s `loosePrompt` — *R — loose at Halvard Reedmere*;
 * - `witness/witness.ts`'s swing prompt — *mouse — strike Halvard Reedmere*;
 * - and the condition line, which has **no key** and therefore no dash.
 *
 * That shape is now drawn rather than merely written: {@link keyOf} cuts the key
 * off the front and it is set as the kit's `kbd` chip, which is what the lab's
 * HUD page does with every prompt on it. It is a rule and not a parse of
 * somebody's prose — the composers put the dash there for a reader, and this
 * reads it back the same way — and where the rule does not hold, the line is
 * simply drawn whole, so the failure mode is a missing chip and never a wrong
 * one.
 *
 * The separator is **imported** from where the first of those composers writes
 * it (`witness/interact.ts`'s `PROMPT_DASH`) rather than spelled again here, so
 * there is one of it; the two modules this chunk's fence does not reach keep
 * their literal, and `dev/selfcheck/telling.ts` holds all four to the same
 * string.
 */

/**
 * The "F — talk to Halvard Reedmere" line under the crosshair.
 *
 * The only thing in the HUD that changes every frame, which is why it writes
 * nothing when the text has not moved: a text node rewritten at frame rate costs
 * more than the raycast that produced it (`hud/hud.ts`'s throttle, same
 * reasoning, cheaper mechanism).
 *
 * Three *instances* since Phase 5, told apart by `variant`, the same way
 * {@link DialogueLine} is. The aimed one sits under the crosshair and names
 * what is in front of the player; the other two sit at the top of the screen
 * and name verbs the *world* has made available rather than something the
 * player is pointing at — the night (`main.ts`, plan decision 8), and word this
 * town has not heard (decision 4). Same anatomy, same change-detection,
 * different place and a different question.
 *
 * The word line is the whole of the share-word flow's advertising, and it is a
 * prompt rather than a panel on purpose: **the deed is the record and there is
 * no reward chrome**, so what the client is allowed to say is that the player
 * is carrying something this town lacks — two facts it already holds, the
 * journal and `rumors(here)` — and where to go and hand it over. It composes
 * nothing about any record.
 */
export class InteractPrompt {
  private readonly element: HTMLElement;

  /**
   * The key chip, in its own element so it can be a `kbd` (chunk U-1).
   *
   * Built once and left in the document like the two nodes beside it: this is
   * the one element in the HUD that changes every frame, and three children that
   * are created and thrown away at that rate would be the cost the whole class
   * is written to avoid.
   */
  private readonly key: HTMLElement;

  /**
   * The crime word, in its own element so it can be its own colour.
   *
   * **The first colour semantics in a prompt** (the owner's ruling of
   * 2026-08-31: *"whenever an action is a crime, change the text to red and say
   * (steal) (assault) (kidnap)"*), and it is kept to the smallest thing that
   * carries the meaning: **the word alone is red**, the verb beside it is not.
   * A whole line in red is a warning about the line; a red word after a verb is
   * a warning about the verb, which is what the ruling asks for — and it leaves
   * the UI-direction conversation a colour to define rather than a scheme to
   * undo.
   *
   * Built once and left in the document like the text node beside it, for the
   * same reason: this is the one element in the HUD that changes every frame.
   */
  private readonly crime: HTMLElement;

  /** The line itself, held as the text node it is so the span survives a rewrite. */
  private readonly line: Text;

  private shown: string | null = null;
  private tagged = "";

  constructor(
    root: HTMLElement,
    variant: "aim" | "camp" | "word" | "swing" | "loose" | "condition" = "aim",
  ) {
    this.element = document.createElement("div");
    // `hud-text` is the kit's shadow for type drawn over the world, composed
    // alongside the client's own name rather than replacing it (plan D2). The
    // aiming prompt gained a modifier of its own at U-1 so the base class could
    // become the shared anatomy and the yield rule could name one variant.
    this.element.className = `hud__prompt hud__prompt--${variant} hud-text`;
    this.element.hidden = true;

    this.key = document.createElement("kbd");
    this.key.className = "hud__prompt-key";
    this.key.hidden = true;

    this.crime = document.createElement("span");
    this.crime.className = "hud__prompt-crime";
    this.crime.hidden = true;

    this.line = document.createTextNode("");
    this.element.append(this.key, this.line, this.crime);
    root.append(this.element);
  }

  /**
   * Shows a prompt, or hides it when handed `null`.
   *
   * `crime` is the word this press would be answered for — `(theft)`,
   * `(abduction)`, `(violence)` — and it is drawn in red after the line. The
   * word is the *simulation's* (`witness/law.ts`'s `crimeTag` over
   * `world/types.ts`'s `crimeWord`); what this class decides is the colour.
   *
   * Written only when something moved, like everything else the frame loop
   * touches: the two halves are compared separately so a prompt whose crime word
   * came and went does not rewrite its verb.
   */
  set(text: string | null, crime = ""): void {
    if (text !== this.shown) {
      this.shown = text;
      this.element.hidden = text === null;

      const cut = keyOf(text);

      this.key.hidden = cut === null;
      this.key.textContent = cut === null ? "" : cut.key;
      // The text node rather than `textContent`, which would take the chip and
      // the crime element out of the document with it.
      this.line.textContent = cut === null ? (text ?? "") : cut.does;
    }

    if (crime !== this.tagged) {
      this.tagged = crime;
      this.crime.hidden = crime === "";
      this.crime.textContent = crime === "" ? "" : ` ${crime}`;
    }
  }
}

/**
 * How long a key's name may be before this stops believing it is one.
 *
 * The longest one any surface names today is the mouse's five letters, and the
 * pad's own spellings — *camp*, *wait*, *rest* — are four. Eight is that with
 * room, and it is a **guard rather than a size**: what it is really refusing is
 * the case where a line has no key at all and the first em-dash in it belongs to
 * something else. A name would fail it on the space before it failed it on the
 * length, and both tests are cheaper than being wrong about which half of a
 * sentence is the key.
 */
const PROMPT_KEY_LETTERS = 8;

/**
 * Cuts a prompt line into the key and what pressing it does, or `null` where
 * there is no key in it.
 *
 * `null` is a real answer and not a failure: the condition line is a sentence
 * about the player's own body with nothing to press (`witness/witness.ts`), and
 * the aiming prompt on a touchscreen names no key either, because there is not
 * one to name (`witness/interact.ts`'s `promptFor`, `input.md` §9). Both are
 * drawn whole, which is what they were before this chip existed.
 */
function keyOf(text: string | null): { readonly key: string; readonly does: string } | null {
  if (text === null) {
    return null;
  }

  const at = text.indexOf(PROMPT_DASH);

  if (at <= 0 || at > PROMPT_KEY_LETTERS) {
    return null;
  }

  const key = text.slice(0, at);

  // A key is one word. Anything with a space in front of the dash is a sentence
  // that happens to contain one, and this must not eat half of it.
  return key.includes(" ") ? null : { key, does: text.slice(at + PROMPT_DASH.length) };
}
